/**
 * CheckpointPicker
 *
 * Dropdown listing curated + installed checkpoints for the Deforum config panel.
 * Shows recommendation hint when topic matches a model's use_cases tags.
 * Not-installed curated models show an inline install confirmation with
 * license summary + size before downloading.
 *
 * Reuses the shared card-with-thumbnail model dropdown (ModelCard, PreviewPanel,
 * useModelDropdownPanel from '@/components/common/model-picker') so this picker
 * and the Image tab's ComfyUI SD 1.5 model picker read as the same component
 * family — see RULE #-2 (single source of truth, no duplicate implementations).
 *
 * RULE #5: fail loud — install failures surface as visible errors, never silently
 * fall back to v1-5-pruned-emaonly.
 * CSS: dark theme only (bg-black/30, text-white/70, border-white/10).
 * RULE #13: mobile-first, 44px touch targets.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, ChevronDown } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { PreviewPanel, SearchPanel, useModelDropdownPanel } from '@/components/common/model-picker';
import type { ImageModelInfo } from '@/types';
import { BrowseCivitAICheckpointsModal } from './BrowseCivitAICheckpointsModal';
import type { CheckpointInfo, CivitAISearchItem, InstallJobStatus } from '@/services/civitaiApi';
import {
  listInstalledCheckpoints,
  installCheckpoint,
  getCheckpointInstallStatus,
  recommendCheckpoint,
  searchCivitAICheckpoints,
} from '@/services/civitaiApi';

// ============================================================================
// Types
// ============================================================================

export interface CheckpointPickerProps {
  value: string;
  /**
   * Called when a checkpoint is selected. `meta` carries the full record (when
   * known) so the parent can apply per-model recommendations such as steps /
   * CFG / sampler — mirroring ImageModelSelector's `meta` pattern. It is
   * undefined only for externally-installed checkpoints with no curated spec.
   */
  onChange: (
    filename: string,
    base_model: 'sd15' | 'sdxl',
    is_hyper: boolean,
    meta?: CheckpointInfo
  ) => void;
  topic?: string;
  disabled?: boolean;
}

type InstallPhase =
  | { phase: 'idle' }
  | { phase: 'confirm'; checkpoint: CheckpointInfo }
  | {
      phase: 'installing';
      checkpoint: CheckpointInfo;
      jobId: string;
      status: InstallJobStatus;
    }
  | { phase: 'error'; checkpoint: CheckpointInfo; message: string };

// ============================================================================
// Helpers
// ============================================================================

function deriveBaseModel(ckpt: CheckpointInfo): 'sd15' | 'sdxl' {
  return ckpt.base_model === 'SDXL 1.0' ? 'sdxl' : 'sd15';
}

function matchesRecommendation(ckpt: CheckpointInfo, topic: string): boolean {
  if (!topic) return false;
  const lower = topic.toLowerCase();
  return ckpt.use_cases.some((tag) => lower.includes(tag.toLowerCase()));
}

/**
 * Maps a Deforum CheckpointInfo record onto the shared ImageModelInfo shape so
 * it can be rendered by the shared ModelCard / PreviewPanel components.
 */
function toImageModelInfo(ckpt: CheckpointInfo): ImageModelInfo {
  return {
    id: ckpt.filename,
    name: ckpt.display_name,
    filename: ckpt.filename,
    description: ckpt.description,
    type: 'checkpoint',
    base: ckpt.base_model ?? '',
    recommended: false, // Deforum has its own separate "recommended for topic" hint banner below — avoid a second, confusing signal on the card itself
    source: ckpt.is_installed ? 'local' : 'download',
    image_url: ckpt.image_url ?? null,
    stats: null,
    creator: ckpt.creator,
    civitai_id: ckpt.civitai_model_id,
    civitai_version_id: ckpt.civitai_version_id,
    is_installed: ckpt.is_installed,
    is_hyper: ckpt.is_hyper,
    size_mb: ckpt.size_mb ?? undefined,
    slug: ckpt.slug ?? undefined,
    nsfw: ckpt.nsfw,
  };
}

/**
 * Maps a live CivitAI search/popular result onto the shared ImageModelInfo
 * shape, source: 'civitai' — rendered as a read-only preview card, exactly
 * like the Image tab's inline CivitAI search results. Installing an arbitrary
 * CivitAI checkpoint still goes through the dedicated "Browse CivitAI" button
 * below (which has its own license-confirm + install flow), not this preview.
 */
function civitaiItemToImageModelInfo(item: CivitAISearchItem): ImageModelInfo {
  return {
    id: `civitai-${item.id}`,
    name: item.name,
    filename: '',
    description: '',
    type: 'checkpoint',
    base: item.base_model,
    recommended: false,
    source: 'civitai',
    image_url: item.thumbnail_url,
    stats: { downloadCount: item.downloads_count, favoriteCount: 0, rating: item.rating ?? 0 },
    creator: item.creator,
    civitai_id: item.id,
    nsfw: item.nsfw,
  };
}

// ============================================================================
// Sub-component: install confirmation inline panel
// ============================================================================

interface InstallConfirmInlineProps {
  checkpoint: CheckpointInfo;
  onAccept: () => void;
  onCancel: () => void;
}

function InstallConfirmInline({ checkpoint, onAccept, onCancel }: InstallConfirmInlineProps) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  return (
    <div className="mt-3 bg-black/40 border border-white/10 rounded-xl p-3 space-y-2">
      <p className="text-white text-sm font-medium">
        {t('deforum.picker.checkpoint.installPrompt.title', { name: checkpoint.display_name })}
      </p>
      <p className="text-white/60 text-xs">
        {t('deforum.picker.checkpoint.installPrompt.size', {
          sizeMb: String(checkpoint.size_mb),
        })}
      </p>
      <p className="text-white/60 text-xs">
        {t('deforum.picker.checkpoint.installPrompt.license', {
          license: checkpoint.license_summary,
        })}
      </p>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          className="w-4 h-4 accent-purple-500"
        />
        <span className="text-white/70 text-xs">
          {t('deforum.picker.checkpoint.installPrompt.acceptLicense')}
        </span>
      </label>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onAccept}
          disabled={!accepted}
          className="flex-1 min-h-[44px] bg-purple-600/80 hover:bg-purple-500/90 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
        >
          {t('deforum.picker.checkpoint.installPrompt.installButton')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 min-h-[44px] bg-white/5 hover:bg-white/10 text-white/70 text-sm rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function CheckpointPicker({
  value,
  onChange,
  topic,
  disabled = false,
}: CheckpointPickerProps) {
  const { t } = useTranslation();
  const [checkpoints, setCheckpoints] = useState<CheckpointInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [recommended, setRecommended] = useState<CheckpointInfo | null>(null);
  const [installState, setInstallState] = useState<InstallPhase>({ phase: 'idle' });
  const [showModal, setShowModal] = useState(false);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ---- Inline CivitAI search/preview (mirrors ImageModelSelector) ----------
  const [searchQuery, setSearchQuery] = useState('');
  const [civitaiModels, setCivitaiModels] = useState<ImageModelInfo[]>([]);
  const [popularModels, setPopularModels] = useState<ImageModelInfo[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [popularLoaded, setPopularLoaded] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isInstalling = installState.phase === 'installing' || installState.phase === 'confirm';
  const { isOpen, panelStyle, triggerRef, panelRef, toggle, close } = useModelDropdownPanel(
    disabled || isInstalling
  );

  // ---- Load catalog --------------------------------------------------------

  const loadCheckpoints = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await listInstalledCheckpoints();
      setCheckpoints(list);
    } catch (err: unknown) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load checkpoints');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCheckpoints();
    return () => {
      if (pollRef.current !== null) clearInterval(pollRef.current);
    };
  }, [loadCheckpoints]);

  // ---- Recommendation hint -------------------------------------------------

  useEffect(() => {
    if (!topic || checkpoints.length === 0) {
      setRecommended(null);
      return;
    }
    // Fast local check first
    const local = checkpoints.find((ck) => matchesRecommendation(ck, topic)) ?? null;
    setRecommended(local);

    // Ask backend for a more accurate recommendation (non-blocking)
    recommendCheckpoint(topic)
      .then((rec) => {
        if (rec !== null) setRecommended(rec);
      })
      .catch(() => {
        // Non-critical; already have local fallback
      });
  }, [topic, checkpoints]);

  // ---- Inline CivitAI search/preview ---------------------------------------

  // Load a "Popular on CivitAI" preview the first time the dropdown opens.
  useEffect(() => {
    if (!isOpen || popularLoaded) return;
    let cancelled = false;
    searchCivitAICheckpoints({})
      .then((resp) => {
        if (!cancelled) setPopularModels(resp.items.map(civitaiItemToImageModelInfo));
      })
      .catch(() => {
        // Non-critical: the preview list is supplementary
      })
      .finally(() => {
        if (!cancelled) setPopularLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, popularLoaded]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    if (!query.trim()) {
      setIsSearching(false);
      setCivitaiModels([]);
      setSearchError(null);
      return;
    }

    setIsSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const resp = await searchCivitAICheckpoints({ query: query.trim() });
        setCivitaiModels(resp.items.map(civitaiItemToImageModelInfo));
        setSearchError(null);
      } catch (err: unknown) {
        setCivitaiModels([]);
        setSearchError(err instanceof Error ? err.message : 'Failed to search models');
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  // ---- Install flow --------------------------------------------------------

  const startInstall = useCallback((checkpoint: CheckpointInfo) => {
    setInstallState({ phase: 'confirm', checkpoint });
  }, []);

  const cancelInstall = useCallback(() => {
    setInstallState({ phase: 'idle' });
  }, []);

  const confirmInstall = useCallback(async () => {
    if (installState.phase !== 'confirm') return;
    const { checkpoint } = installState;

    try {
      const { job_id } = await installCheckpoint({
        slug: checkpoint.slug,
        civitai_version_id: checkpoint.civitai_version_id,
      });

      const initialStatus: InstallJobStatus = {
        status: 'queued',
        progress_mb: 0,
        total_mb: checkpoint.size_mb,
        error: null,
      };
      setInstallState({ phase: 'installing', checkpoint, jobId: job_id, status: initialStatus });

      if (pollRef.current !== null) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const status = await getCheckpointInstallStatus(job_id);
          setInstallState((prev) => (prev.phase === 'installing' ? { ...prev, status } : prev));

          if (status.status === 'installed') {
            if (pollRef.current !== null) clearInterval(pollRef.current);
            setInstallState({ phase: 'idle' });
            // Refresh catalog and auto-select
            await loadCheckpoints();
            onChange(
              checkpoint.filename,
              deriveBaseModel(checkpoint),
              checkpoint.is_hyper,
              checkpoint
            );
          } else if (status.status === 'failed') {
            if (pollRef.current !== null) clearInterval(pollRef.current);
            setInstallState({
              phase: 'error',
              checkpoint,
              message: status.error ?? 'Install failed',
            });
          }
        } catch (err: unknown) {
          if (pollRef.current !== null) clearInterval(pollRef.current);
          setInstallState({
            phase: 'error',
            checkpoint,
            message: err instanceof Error ? err.message : 'Install failed',
          });
        }
      }, 2000);
    } catch (err: unknown) {
      setInstallState({
        phase: 'error',
        checkpoint,
        message: err instanceof Error ? err.message : 'Install failed',
      });
    }
  }, [installState, onChange, loadCheckpoints]);

  // ---- Selection -----------------------------------------------------------

  const handleImageBroken = useCallback((key: string) => {
    setBrokenImages((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  const handleCardSelect = useCallback(
    (item: ImageModelInfo) => {
      const ckpt = checkpoints.find((c) => c.filename === item.id);
      if (!ckpt) return;

      if (!ckpt.is_installed) {
        startInstall(ckpt);
      } else {
        onChange(ckpt.filename, deriveBaseModel(ckpt), ckpt.is_hyper, ckpt);
      }
      close();
    },
    [checkpoints, startInstall, onChange, close]
  );

  const handleUseRecommended = useCallback(() => {
    if (!recommended) return;
    if (!recommended.is_installed) {
      startInstall(recommended);
      return;
    }
    onChange(recommended.filename, deriveBaseModel(recommended), recommended.is_hyper, recommended);
  }, [recommended, startInstall, onChange]);

  const handleModalInstalled = useCallback(
    (filename: string, base_model: 'sd15' | 'sdxl', is_hyper: boolean) => {
      setShowModal(false);
      void loadCheckpoints();
      onChange(filename, base_model, is_hyper);
    },
    [loadCheckpoints, onChange]
  );

  // ---- Partition checkpoints into sections --------------------------------

  const curated = checkpoints.filter((c) => c.is_curated);
  const otherInstalled = checkpoints.filter((c) => !c.is_curated && c.is_installed);
  const selectedCkpt = value ? checkpoints.find((c) => c.filename === value) : undefined;

  // ---- Render --------------------------------------------------------------

  return (
    <div className="space-y-2">
      {/* Label row — icon + label matches ImageModelSelector (Fooocus) so the
          two checkpoint pickers read as the same component family. */}
      <label className="form-label flex items-center gap-2">
        <Box className="h-4 w-4 flex-shrink-0" />
        <span>{t('deforum.picker.checkpoint.label')}</span>
      </label>

      {/* Dropdown */}
      {loading ? (
        <div className="input bg-black/20 text-white/40 border-white/10 min-h-[44px] w-full flex items-center px-3 text-sm rounded-lg">
          Loading checkpoints…
        </div>
      ) : loadError ? (
        <div className="text-red-400 text-xs py-1">{loadError}</div>
      ) : (
        <>
          {/* Dropdown trigger */}
          <button
            ref={triggerRef}
            type="button"
            onClick={toggle}
            disabled={disabled || isInstalling}
            className={`w-full flex items-center justify-between min-h-[44px] px-3 py-2.5 rounded-lg border transition-all text-left ${
              isOpen
                ? 'border-primary-500 ring-2 ring-primary-500/30'
                : 'border-white/10 hover:border-white/20'
            } bg-black/20 text-white ${
              disabled || isInstalling ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              {selectedCkpt?.image_url && (
                <img
                  src={selectedCkpt.image_url}
                  alt=""
                  className="w-6 h-6 rounded object-cover flex-shrink-0"
                />
              )}
              <span className="truncate text-sm">
                {selectedCkpt?.display_name || 'Select checkpoint…'}
              </span>
            </div>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-white/50 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Dropdown panel - portaled to body to escape overflow clipping */}
          {isOpen &&
            createPortal(
              <div
                ref={panelRef}
                style={panelStyle}
                className="bg-[#1a1028] border border-white/10 rounded-lg shadow-2xl overflow-hidden"
              >
                <SearchPanel
                  models={curated.map(toImageModelInfo)}
                  extraModels={otherInstalled.map(toImageModelInfo)}
                  extraModelsLabel={t('deforum.picker.checkpoint.installedSection')}
                  curatedSectionLabel={t('deforum.picker.checkpoint.curatedSection')}
                  civitaiModels={civitaiModels}
                  popularModels={popularModels}
                  selectedValue={value}
                  searchQuery={searchQuery}
                  isSearching={isSearching}
                  searchError={searchError}
                  popularLoaded={popularLoaded}
                  brokenImages={brokenImages}
                  onSearchChange={handleSearchChange}
                  onSelect={handleCardSelect}
                  onImageBroken={handleImageBroken}
                />
              </div>,
              document.body
            )}
        </>
      )}

      {/* Selected model detail card */}
      {selectedCkpt && !isOpen && (selectedCkpt.image_url || selectedCkpt.description) && (
        <PreviewPanel
          model={toImageModelInfo(selectedCkpt)}
          brokenImages={brokenImages}
          onImageBroken={handleImageBroken}
        />
      )}

      {/* Recommendation hint */}
      {recommended && recommended.filename !== value && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 bg-purple-900/20 border border-purple-500/20 rounded-lg px-3 py-2">
          <span className="text-purple-300 text-xs flex-1">
            {t('deforum.picker.checkpoint.recommendedBadge', { name: recommended.display_name })}
          </span>
          <button
            type="button"
            onClick={handleUseRecommended}
            disabled={disabled}
            className="min-h-[44px] px-3 bg-purple-600/60 hover:bg-purple-500/80 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-40 whitespace-nowrap"
          >
            {t('deforum.picker.checkpoint.useRecommended')}
          </button>
        </div>
      )}

      {/* Install: confirm */}
      {installState.phase === 'confirm' && (
        <InstallConfirmInline
          checkpoint={installState.checkpoint}
          onAccept={confirmInstall}
          onCancel={cancelInstall}
        />
      )}

      {/* Install: progress */}
      {installState.phase === 'installing' && (
        <div className="bg-black/40 border border-white/10 rounded-xl p-3 space-y-2">
          <p className="text-white/70 text-xs">
            {t('deforum.picker.checkpoint.installProgress', {
              progress: String(Math.round(installState.status.progress_mb)),
              total: String(Math.round(installState.status.total_mb)),
            })}
          </p>
          <div className="w-full bg-white/10 rounded-full h-1.5">
            <div
              className="bg-purple-500 h-1.5 rounded-full transition-all"
              style={{
                width:
                  installState.status.total_mb > 0
                    ? `${Math.min(100, (installState.status.progress_mb / installState.status.total_mb) * 100)}%`
                    : '0%',
              }}
            />
          </div>
        </div>
      )}

      {/* Install: error — fail loud, RULE #5 */}
      {installState.phase === 'error' && (
        <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-3 space-y-2">
          <p className="text-red-400 text-xs">
            {t('deforum.picker.checkpoint.installFailed', {
              error: installState.message,
            })}
          </p>
          <button
            type="button"
            onClick={cancelInstall}
            className="text-white/50 hover:text-white text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Browse CivitAI button */}
      <button
        type="button"
        onClick={() => setShowModal(true)}
        disabled={disabled}
        className="min-h-[44px] w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-sm rounded-lg transition-colors disabled:opacity-40"
      >
        {t('deforum.picker.checkpoint.browseCivitAI')} →
      </button>

      {/* CivitAI browse modal */}
      <BrowseCivitAICheckpointsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onInstalled={handleModalInstalled}
      />
    </div>
  );
}
